import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./redux/hooks/redux-hooks";
import ToastMsg from "./components/ToastMsg";

// utils
import { getStorageValue } from "./utility/utils";
import { appConfig } from "./utility/config";

// Actions
import {
  logOutUser,
  setLoggedInUser,
  validateLoggedInUser,
} from "./redux/actions/UserActions";
import { IPublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import Loader from "./components/loader/Loader";
import { CustomNavigationClient } from "./utility/NavigationClient";
import Router from "./Router";

type AppProps = {
  pca: IPublicClientApplication;
};

const App = ({ pca }: AppProps) => {
  const dispatch = useAppDispatch();
  const displayToast = useAppSelector((state) => state.toast.displayToast);
  const isFetchingUser = useAppSelector((state) => state.user.isFetching);

  // The next 3 lines are optional. This is how you configure MSAL to take advantage of the router's navigate functions when MSAL redirects between pages in your app
  const navigate = useNavigate();
  const navigationClient = new CustomNavigationClient(navigate);
  pca.setNavigationClient(navigationClient);

  const loginUserDetails = getStorageValue(appConfig.loginUserStorageKey);

  // To call the validate login user details and store it in storage when login user exists
  useEffect(() => {
    const validateLoginUser = async () => {
      const activeAccount = pca.getActiveAccount();
      const logOutUserHandler = () => {
        displayToast("Invalid Email or Password", "error");
        dispatch(logOutUser());
        pca.logoutRedirect();
      };
      if (!loginUserDetails && !isFetchingUser && activeAccount) {
        try {
          const response: any = await dispatch(
            validateLoggedInUser(
              activeAccount.username,
              appConfig.loginPassword
            )
          );
          if (!response?.loggedIn) {
            logOutUserHandler();
          }
        } catch (error) {
          logOutUserHandler();
        }
      }
    };

    validateLoginUser();
  }, [loginUserDetails, isFetchingUser, dispatch, displayToast, pca]);

  // To set the login user details from local storage
  useEffect(() => {
    const loginUserDetails = getStorageValue(appConfig.loginUserStorageKey);
    if (loginUserDetails) {
      dispatch(setLoggedInUser(loginUserDetails));
    }
  }, [dispatch]);

  return (
    <MsalProvider instance={pca}>
      <div className="wrapper">
        <Router />
        <ToastMsg />
        {isFetchingUser ? <Loader /> : null}
      </div>
    </MsalProvider>
  );
};

export default App;
